import { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { translations, scrollToSection, type Language } from '@/lib/index';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface LayoutProps {
  children: React.ReactNode;
  language: Language;
  setLanguage: (lang: Language) => void;
}

export function Layout({ children, language, setLanguage }: LayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const t = translations[language];

  const languageLabels: Record<Language, string> = {
    ca: 'CAT',
    es: 'ES',
    en: 'EN'
  };

  const navItems = [
    { label: t.nav.about, href: '#about' },
    { label: t.nav.solution, href: '#solution' },
    { label: t.nav.services, href: '#services' },
    { label: t.nav.sustainability, href: '#sustainability' },
    { label: t.nav.contact, href: '#contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };

    handleScroll();
    updateHeight();

    window.addEventListener('scroll', handleScroll);
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, []);

  const handleNavClick = (href: string) => {
    const sectionId = href.replace('#', '');
    scrollToSection(sectionId);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background/80 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <a
              href="#hero"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('hero');
              }}
              className="flex items-center gap-2 group"
            >
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center group-hover:bg-accent transition-colors">
                <Zap className="w-6 h-6 text-primary-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold text-foreground">SEF</span>
                <span className="text-xs text-muted-foreground hidden sm:block">
                  {t.footer.slogan}
                </span>
              </div>
            </a>

            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item.href);
                  }}
                  className="px-3 py-2 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-muted rounded-md transition-colors"
                >
                  {item.label}
                </a>
              ))}
            </nav>

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1">
                    {languageLabels[language]}
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setLanguage('ca')}>
                    Català
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLanguage('es')}>
                    Español
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLanguage('en')}>
                    English
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </Button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden border-t border-border bg-background/95 backdrop-blur-md"
            >
              <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
                {navItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(item.href);
                    }}
                    className="px-4 py-3 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-muted rounded-md transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main style={{ paddingTop: `${headerHeight}px` }}>{children}</main>

      <footer className="bg-sidebar text-sidebar-foreground mt-auto">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-sidebar-primary rounded-lg flex items-center justify-center">
                  <Zap className="w-6 h-6 text-sidebar-primary-foreground" />
                </div>
                <div className="flex flex-col">
                  <span className="text-lg font-bold">SEF</span>
                  <span className="text-xs text-sidebar-foreground/70">
                    {t.footer.slogan}
                  </span>
                </div>
              </div>
              <p className="text-sm text-sidebar-foreground/70">
                {t.about.description}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-4">{t.location.title}</h3>
              <div className="text-sm text-sidebar-foreground/70 space-y-1">
                <p>{t.location.address}</p>
                <p>{t.location.city}</p>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-4">{t.contact.title}</h3>
              <div className="text-sm text-sidebar-foreground/70 space-y-2">
                <p>
                  <span className="font-medium">Email:</span>{' '}
                  <a
                    href={`mailto:${t.contact.info.email}`}
                    className="hover:text-sidebar-primary transition-colors"
                  >
                    {t.contact.info.email}
                  </a>
                </p>
                <p>
                  <span className="font-medium">{t.contact.form.phone}:</span>{' '}
                  <a
                    href={`tel:${t.contact.info.phone}`}
                    className="hover:text-sidebar-primary transition-colors"
                  >
                    {t.contact.info.phone}
                  </a>
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-sidebar-border text-center text-sm text-sidebar-foreground/70">
            <p>{t.footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}